/**
 * Canonical endpoint paths. Importing these from both the NestJS
 * controllers and the React client guarantees the two never drift.
 */
export const API_PREFIX = 'api/v1';

export const ROUTES = {
  health: 'health',

  platform: {
    /** Requires the `X-Platform-Token` header. */
    tenants: 'platform/tenants',
    tenant: (id: string) => `platform/tenants/${id}`,
    /** Deprovision: drops the tenant database and its registry row. */
    tenantDelete: (id: string) => `platform/tenants/${id}`,
    tenantSuspend: (id: string) => `platform/tenants/${id}/suspend`,
    tenantActivate: (id: string) => `platform/tenants/${id}/activate`,
    tenantMigrate: (id: string) => `platform/tenants/${id}/migrate`,
    tenantMigrateAll: 'platform/tenants/migrate-all',
    tenantStats: (id: string) => `platform/tenants/${id}/stats`,
    tenantAudit: (id: string) => `platform/tenants/${id}/audit`,
  },

  auth: {
    login: 'auth/login',
    /** Second step of a two-factor sign-in: the e-mailed code. */
    verifyTwoFactor: 'auth/login/verify',
    /**
     * Self-service sign-up. Writes the account and returns a ticket to choose
     * a country; the workspace database does not exist until it is chosen.
     */
    register: 'auth/register',
    /** Resume a sign-up that has not chosen a country yet. */
    onboarding: 'auth/onboarding',
    /** Choose the country: builds the workspace and returns a session. */
    chooseCountry: 'auth/onboarding/country',
    forgotPassword: 'auth/password/forgot',
    resetPassword: 'auth/password/reset',
    /**
     * Which account a reset link belongs to, so the page can name it before
     * asking for a new password. A separate path rather than a GET on
     * `resetPassword`, because the token must stay out of the query string.
     */
    passwordResetInfo: 'auth/password/reset/info',
    /** Which captcha the client should render. */
    captcha: 'auth/captcha',
    /** Read or change the e-mail two-factor preference. */
    twoFactor: 'auth/two-factor',
    refresh: 'auth/refresh',
    logout: 'auth/logout',
    me: 'auth/me',
    changePassword: 'auth/password',
  },

  /** Public: the countries a workspace can be created in. */
  countries: 'countries',

  settings: 'settings',
  setting: (key: string) => `settings/${key}`,
  /**
   * The workspace's own country: its tax rates, regimes, treatments,
   * identifier types, jurisdictions, document types and settings fields.
   *
   * A distinct route rather than a setting document, because it is not
   * company data — it is reference data the interface is built from, and it is
   * readable by every signed-in user rather than by `settings:write` alone.
   */
  settingsCountry: 'settings/country',

  /**
   * Destroy the signed-in user's own workspace.
   *
   * A literal path, and it must be declared in the controller *before*
   * `settings/:key`, or the parameterised route would swallow it and answer
   * with a setting document called "workspace". `common/literal-route-order.spec.ts`
   * guards that ordering.
   *
   * A sibling of `settings` rather than a child of it, because it does not
   * read or write a setting document: it deletes the database those documents
   * live in.
   */
  settingsWorkspace: 'settings/workspace',

  users: 'users',
  user: (id: string) => `users/${id}`,

  /* The catalog lives under its own segment so that the resources it owns
     (units, categories, products) stay grouped in the route table. */
  units: 'catalog/units',
  unit: (id: string) => `catalog/units/${id}`,

  categories: 'catalog/categories',
  category: (id: string) => `catalog/categories/${id}`,
  categoryTree: 'catalog/categories/tree',

  products: 'catalog/products',
  product: (id: string) => `catalog/products/${id}`,
  productStock: (id: string) => `catalog/products/${id}/stock`,
  productsBulkPrice: 'catalog/products/bulk-price',

  warehouses: 'warehouses',
  warehouse: (id: string) => `warehouses/${id}`,

  stock: 'stock',
  stockValuation: 'stock/valuation',
  stockAdjust: 'stock/adjust',
  movements: 'stock/movements',

  documents: 'documents',
  document: (id: string) => `documents/${id}`,
  documentPost: (id: string) => `documents/${id}/post`,
  documentUnpost: (id: string) => `documents/${id}/unpost`,
  documentCancel: (id: string) => `documents/${id}/cancel`,
  documentPreview: (id: string) => `documents/${id}/preview`,
  documentSummary: 'documents/summary',
  /**
   * Editing presence, as a lease rather than a chat-style session.
   *
   * A PUT because the caller is asking for exactly one state — "I am editing
   * this" — and it is idempotent while the lease lasts. It is renewed by the
   * same call, which is what keeps a closed browser from holding a document.
   */
  documentEditing: (id: string) => `documents/${id}/editing`,

  counterparties: 'counterparties',
  counterparty: (id: string) => `counterparties/${id}`,
  /**
   * Ask the country's registry who holds a tax identifier.
   *
   * Declared before `counterparty(id)` at the controller, because the
   * parameterised route would otherwise swallow it — the same trap
   * `settings/country` sits in.
   */
  counterpartyLookup: 'counterparties/lookup',

  dashboard: 'dashboard/summary',
} as const;

/** Header used by the SPA to hint the tenant before a JWT exists. */
export const TENANT_HEADER = 'x-tenant-slug';
/** Header used for platform-operator calls. */
export const PLATFORM_TOKEN_HEADER = 'x-platform-token';
/** Header used to request a specific response language. */
export const LOCALE_HEADER = 'x-locale';
